// Strip trailing slash from API URL to prevent double-slash issues (e.g., //api/v1)
const rawApiUrl = import.meta.env.VITE_API_URL || "http://localhost:8800";
const API_BASE = rawApiUrl.endsWith('/') ? rawApiUrl.slice(0, -1) : rawApiUrl;
const API_PREFIX = "/api/v1";

export const BACKEND_API = {
  AUTH: {
    LOGIN: `${API_BASE}${API_PREFIX}/auth/login`,
    REGISTER: `${API_BASE}${API_PREFIX}/auth/register`,
    RESET_PASSWORD: `${API_BASE}${API_PREFIX}/auth/reset-password`,
  },

  USERS: {
    CREATE: `${API_BASE}${API_PREFIX}/users`,
    GET_ALL: `${API_BASE}${API_PREFIX}/users`,
    GET_BY_ID: (userId: string | number) => `${API_BASE}${API_PREFIX}/users/${userId}`,
  },

  UPLOADS: {
    INITIATE: `${API_BASE}${API_PREFIX}/uploads/initiate`,
    STATUS: (uploadId: string) => `${API_BASE}${API_PREFIX}/uploads/${uploadId}/status`,
  },

  JOBS: {
    CREATE: `${API_BASE}${API_PREFIX}/jobs`,
    GET_ALL: `${API_BASE}${API_PREFIX}/jobs`,
    GET_BY_ID: (jobId: string | number) => `${API_BASE}${API_PREFIX}/jobs/${jobId}`,
  },

  PGX: {
    RUN: `${API_BASE}${API_PREFIX}/pgx/jobs/run`, 
    STATUS: (jobId: string | number) => `${API_BASE}${API_PREFIX}/pgx/jobs/${jobId}/status`,
    RESULTS: (jobId: string | number) => `${API_BASE}${API_PREFIX}/pgx/jobs/${jobId}/results`,
    PREVIOUS_FILES: `${API_BASE}${API_PREFIX}/pgx/files/previous`,
  },

  // S3 uploads for PropEx pipeline (PGX Extractor)
  S3_PROPEX: {
    PRESIGN: `${API_BASE}${API_PREFIX}/s3-propex/uploads/presign`,
    MULTIPART_INITIATE: `${API_BASE}${API_PREFIX}/s3-propex/uploads/multipart/initiate`,
    MULTIPART_PRESIGN_PART: `${API_BASE}${API_PREFIX}/s3-propex/uploads/multipart/presign-part`,
    MULTIPART_COMPLETE: `${API_BASE}${API_PREFIX}/s3-propex/uploads/multipart/complete`,
    CONFIRM_UPLOAD: `${API_BASE}${API_PREFIX}/s3-propex/uploads/confirm-upload`,
    SUBMIT_CLOUD_URL: `${API_BASE}${API_PREFIX}/s3-propex/uploads/submit-cloud-url`,
    TRANSFER_FROM_URL: `${API_BASE}${API_PREFIX}/s3-propex/uploads/transfer-from-url`,
    TRANSFER_STATUS: (transferId: string) => `${API_BASE}${API_PREFIX}/s3-propex/uploads/transfer-from-url/${transferId}`,
  },

  HAPLOTYPE_CALLER: {
    RUN: `${API_BASE}${API_PREFIX}/haplotype-caller/jobs/run`,
    STATUS: (jobId: string | number) => `${API_BASE}${API_PREFIX}/haplotype-caller/jobs/${jobId}/status`,
    RESULTS: (jobId: string | number) => `${API_BASE}${API_PREFIX}/haplotype-caller/jobs/${jobId}/results`,
    CANCEL: (jobId: string | number) => `${API_BASE}${API_PREFIX}/haplotype-caller/jobs/${jobId}/cancel`,
    LIST: `${API_BASE}${API_PREFIX}/haplotype-caller/jobs/list`,
    PREVIOUS_FILES: `${API_BASE}${API_PREFIX}/haplotype-caller/files/previous`,
  },

  HLA_TYPING: {
    RUN: `${API_BASE}${API_PREFIX}/hla-typing/jobs/run`,
    STATUS: (jobId: string | number) => `${API_BASE}${API_PREFIX}/hla-typing/jobs/${jobId}/status`,
    RESULTS: (jobId: string | number) => `${API_BASE}${API_PREFIX}/hla-typing/jobs/${jobId}/results`,
    CANCEL: (jobId: string | number) => `${API_BASE}${API_PREFIX}/hla-typing/jobs/${jobId}/cancel`,
    PDF: (jobId: string | number) => `${API_BASE}${API_PREFIX}/hla-typing/jobs/${jobId}/pdf`,
    MULTIQC_ZIP: (jobId: string | number) => `${API_BASE}${API_PREFIX}/hla-typing/jobs/${jobId}/multiqc-zip`,
    LIST: `${API_BASE}${API_PREFIX}/hla-typing/jobs/list`,
    PREVIOUS_FILES: `${API_BASE}${API_PREFIX}/hla-typing/files/previous`,
  },

  // S3 uploads for HLA Typing pipeline
  S3_HLA: {
    PRESIGN: `${API_BASE}${API_PREFIX}/s3-hla/uploads/presign`,
    MULTIPART_INITIATE: `${API_BASE}${API_PREFIX}/s3-hla/uploads/multipart/initiate`,
    MULTIPART_PRESIGN_PART: `${API_BASE}${API_PREFIX}/s3-hla/uploads/multipart/presign-part`,
    MULTIPART_COMPLETE: `${API_BASE}${API_PREFIX}/s3-hla/uploads/multipart/complete`,
    CONFIRM_UPLOAD: `${API_BASE}${API_PREFIX}/s3-hla/uploads/confirm-upload`,
    TRANSFER_FROM_URL: `${API_BASE}${API_PREFIX}/s3-hla/uploads/transfer-from-url`,
    TRANSFER_STATUS: (transferId: string) => `${API_BASE}${API_PREFIX}/s3-hla/uploads/transfer-from-url/${transferId}`,
  },

  // VCF2TXT Pipeline
  VCF2TXT: {
    RUN: `${API_BASE}${API_PREFIX}/vcf2txt/jobs/run`,
    STATUS: (jobId: string | number) => `${API_BASE}${API_PREFIX}/vcf2txt/jobs/${jobId}/status`,
    RESULTS: (jobId: string | number) => `${API_BASE}${API_PREFIX}/vcf2txt/jobs/${jobId}/results`,
    CANCEL: (jobId: string | number) => `${API_BASE}${API_PREFIX}/vcf2txt/jobs/${jobId}/cancel`,
    LIST: `${API_BASE}${API_PREFIX}/vcf2txt/jobs/list`,
    PREVIOUS_FILES: `${API_BASE}${API_PREFIX}/vcf2txt/files/previous`,
  },

  // S3 uploads for VCF2TXT pipeline
  S3_VCF2TXT: {
    PRESIGN: `${API_BASE}${API_PREFIX}/s3-vcf2txt/uploads/presign`,
    MULTIPART_INITIATE: `${API_BASE}${API_PREFIX}/s3-vcf2txt/uploads/multipart/initiate`,
    MULTIPART_PRESIGN_PART: `${API_BASE}${API_PREFIX}/s3-vcf2txt/uploads/multipart/presign-part`,
    MULTIPART_COMPLETE: `${API_BASE}${API_PREFIX}/s3-vcf2txt/uploads/multipart/complete`,
    CONFIRM_UPLOAD: `${API_BASE}${API_PREFIX}/s3-vcf2txt/uploads/confirm-upload`,
    TRANSFER_FROM_URL: `${API_BASE}${API_PREFIX}/s3-vcf2txt/uploads/transfer-from-url`,
    TRANSFER_STATUS: (transferId: string) => `${API_BASE}${API_PREFIX}/s3-vcf2txt/uploads/transfer-from-url/${transferId}`,
  },

  // Feedback
  FEEDBACK: {
    SUBMIT: `${API_BASE}${API_PREFIX}/feedback/submit`,
    CHECK: (jobId: string | number) => `${API_BASE}${API_PREFIX}/feedback/check/${jobId}`,
    LIST: `${API_BASE}${API_PREFIX}/feedback/list`,
    STATS: `${API_BASE}${API_PREFIX}/feedback/stats`,
  },

  // User Profile & Onboarding
  USER: {
    ME: `${API_BASE}${API_PREFIX}/users/me`,
    ONBOARDING_STATUS: `${API_BASE}${API_PREFIX}/users/onboarding/status`,
    ONBOARDING_COMPLETE: `${API_BASE}${API_PREFIX}/users/onboarding`,
  },
};
